import type { ManagerialDashboardFinance } from '@/types/api'
import { INVESTOR_METRICS, formatFinancialMonth } from '../../lib/executive-finance'
import { formatFullCurrencyBrl } from '../../lib/managerial-kpis'
import { KpiInfoHint } from '../KpiCard/KpiInfoHint'
import styles from './ExecutiveFinance.module.css'

export function InvestorMetrics({
  finance,
  isLoading,
}: {
  finance: ManagerialDashboardFinance | undefined
  isLoading: boolean
}) {
  return (
    <section
      className={styles.metrics}
      aria-label="Indicadores para investidores"
      aria-busy={isLoading}
    >
      <p className={styles.scopeNote}>
        Indicadores em vermelho ainda não têm base suficiente. Consulte as medições
        gerenciais já disponíveis no painel; elas não substituem projeções ou registros de
        caixa.
      </p>
      {(['financial', 'operational'] as const).map((group) => (
        <div key={group}>
          <div className={styles.sectionHeading}>
            <h2>
              {group === 'financial'
                ? 'Indicadores financeiros'
                : 'Aquisição e relacionamento'}
            </h2>
            <span>
              {group === 'financial'
                ? 'Receita, eficiência e caixa'
                : `Consolidado da plataforma · CAC ${finance ? formatFinancialMonth(finance.reference_month) : 'do mês atual'}`}
            </span>
          </div>
          <div className={styles.metricGrid}>
            {INVESTOR_METRICS.filter((metric) => metric.group === group).map((metric) => {
              const isCac = metric.id === 'cac'
              const marketingRecorded =
                finance?.marketing_recorded ?? (finance?.marketing_spend ?? 0) > 0
              const hasValue =
                isCac &&
                finance !== undefined &&
                finance.cac !== null &&
                finance.new_customers > 0 &&
                marketingRecorded
              const displayValue =
                hasValue && finance?.cac !== null && finance?.cac !== undefined
                  ? formatFullCurrencyBrl(finance.cac)
                  : '—'
              const note =
                isCac && finance
                  ? !marketingRecorded
                    ? 'Registre o investimento do mês em Marketing e aquisição nos custos operacionais, inclusive quando for zero.'
                    : finance.marketing_spend === 0
                      ? `Investimento registrado: R$ 0,00 · ${formatFinancialMonth(finance.reference_month)}`
                      : `${formatFullCurrencyBrl(finance.marketing_spend)} em marketing · ${finance.new_customers} novas instituições`
                  : metric.pending
              return (
                <article
                  key={metric.id}
                  className={`${styles.metric} ${!hasValue && !isLoading ? styles.unavailable : ''}`}
                  aria-label={`${metric.label}: ${hasValue ? displayValue : 'Sem base'}`}
                >
                  <div className={styles.metricHeading}>
                    <h3>{metric.label}</h3>
                    <KpiInfoHint
                      kpiId={`investor-${metric.id}`}
                      placeAbove={false}
                      criterion={{
                        title: metric.label,
                        description: metric.description,
                        illustrativeNote: null,
                      }}
                    />
                  </div>
                  <p className={styles.metricName}>{metric.name}</p>
                  <strong
                    className={
                      hasValue
                        ? styles.measured
                        : isLoading
                          ? styles.unmeasured
                          : styles.negative
                    }
                  >
                    {displayValue}
                  </strong>
                  {!hasValue && !isLoading ? (
                    <p className={styles.unavailableLabel}>Sem base de cálculo</p>
                  ) : null}
                  <p className={styles.metricNote}>
                    {isCac &&
                    finance !== undefined &&
                    !hasValue &&
                    finance.new_customers === 0 &&
                    marketingRecorded
                      ? `Sem novas instituições · ${note}`
                      : note}
                  </p>
                </article>
              )
            })}
          </div>
        </div>
      ))}
    </section>
  )
}
